import { useState, useRef, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { API } from '@/App';

const AffinitySelector = ({ selectedIds = [], excludeId, onChange }) => {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [adolescents, setAdolescents] = useState([]);
  const dropdownRef = useRef(null);

  // Fetch adolescents on mount
  useEffect(() => {
    const fetchAdolescents = async () => {
      try {
        const response = await axios.get(`${API}/adolescents`);
        setAdolescents(response.data || []);
      } catch (error) {
        console.error('Error fetching adolescents:', error);
        setAdolescents([]);
      }
    };
    fetchAdolescents();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearch('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Ensure selectedIds is always an array
  const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];

  // Filter adolescents based on search
  const filteredAdolescents = (adolescents || []).filter(a => {
    // Skip current adolescent and already selected
    if (a.id === excludeId || safeSelectedIds.includes(a.id)) return false;
    
    const searchTerm = search.toLowerCase().trim();
    if (searchTerm.length < 2) return false;
    
    // Search in name
    const nameMatch = a.name && a.name.toLowerCase().includes(searchTerm);
    
    // Search in CPF (remove all non-digits)
    const cpfClean = a.cpf ? a.cpf.replace(/\D/g, '') : '';
    const searchClean = search.replace(/\D/g, '');
    const cpfMatch = searchClean.length > 0 && cpfClean.includes(searchClean);
    
    // Search in guardian name
    const guardianMatch = a.guardian_name && a.guardian_name.toLowerCase().includes(searchTerm);
    
    // Search in phone (remove all non-digits)
    const phoneClean = a.phone ? a.phone.replace(/\D/g, '') : '';
    const phoneMatch = searchClean.length > 0 && phoneClean.includes(searchClean);
    
    return nameMatch || cpfMatch || guardianMatch || phoneMatch;
  });

  const handleAdd = (id) => {
    if (safeSelectedIds.length < 3 && !safeSelectedIds.includes(id)) {
      onChange([...safeSelectedIds, id]);
      setSearch('');
      setTimeout(() => setShowDropdown(false), 100);
    }
  };

  const handleRemove = (id) => {
    onChange(safeSelectedIds.filter(selectedId => selectedId !== id));
  };

  const selectedAdolescents = safeSelectedIds
    .map(id => adolescents.find(a => a.id === id))
    .filter(Boolean);

  return (
    <div className="space-y-3">
      {/* Search Input */}
      {safeSelectedIds.length < 3 && (
        <div className="relative" ref={dropdownRef}>
          <Label className="mb-2 block text-sm font-medium text-gray-700">
            Digite para buscar adolescentes
          </Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Digite nome ou CPF (mínimo 2 caracteres)..."
              value={search}
              onChange={(e) => {
                const value = e.target.value;
                setSearch(value);
                if (value.length > 1) {
                  setShowDropdown(true);
                }
              }}
              onFocus={() => {
                if (search.length > 1) {
                  setShowDropdown(true);
                }
              }}
              className="pl-10"
              data-testid="affinity-search"
            />
          </div>

          {/* Dropdown Results */}
          {showDropdown && search.length > 1 && (
            <div 
              className="absolute z-[9999] w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-2xl max-h-80 overflow-y-auto"
              style={{ position: 'absolute', top: '100%' }}
            >
              {filteredAdolescents.length > 0 ? (
                <div>
                  <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 sticky top-0">
                    <p className="text-sm font-semibold text-blue-700">
                      {filteredAdolescents.length} resultado(s) encontrado(s)
                    </p>
                  </div>
                  {filteredAdolescents.slice(0, 20).map((adolescent) => (
                    <div
                      key={adolescent.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAdd(adolescent.id);
                      }}
                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0 transition"
                      data-testid="affinity-option"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 text-base">{adolescent.name}</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <span className="text-xs text-gray-600">
                              {adolescent.age} anos
                            </span>
                            {adolescent.cpf && (
                              <span className="text-xs text-gray-600">
                                • CPF: {adolescent.cpf}
                              </span>
                            )}
                          </div>
                          {adolescent.guardian_name && (
                            <p className="text-xs text-gray-500 mt-1">
                              Responsável: {adolescent.guardian_name}
                            </p>
                          )}
                        </div>
                        <span className="text-blue-600 hover:text-blue-700 font-semibold text-sm px-3 py-1 bg-blue-50 rounded whitespace-nowrap ml-3">
                          Adicionar
                        </span>
                      </div>
                    </div>
                  ))}
                  {filteredAdolescents.length > 20 && (
                    <div className="px-4 py-2 text-sm text-gray-500 text-center bg-gray-50 sticky bottom-0 border-t border-gray-200">
                      +{filteredAdolescents.length - 20} mais resultados. Continue digitando para refinar...
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  <p className="font-semibold">Nenhum resultado para "{search}"</p>
                  <p className="text-sm mt-1">Digite pelo menos 2 caracteres</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Selected Tags */}
      {selectedAdolescents.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedAdolescents.map((adolescent) => (
            <div
              key={adolescent.id}
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 px-4 py-2 rounded-full border border-blue-200 shadow-sm"
              data-testid="selected-affinity"
            >
              <div className="flex flex-col">
                <span className="font-semibold text-sm">{adolescent.name}</span>
                <span className="text-xs text-blue-600">{adolescent.age} anos</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(adolescent.id)}
                className="hover:bg-blue-200 rounded-full p-1 transition"
                data-testid="remove-affinity"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Counter */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {safeSelectedIds.length}/3 adolescentes selecionados
        </span>
        {safeSelectedIds.length === 3 && (
          <span className="text-green-600 font-semibold">✓ Máximo atingido</span>
        )}
      </div>
    </div>
  );
};

export default AffinitySelector;
